import { defineStore } from "pinia";
import { baseURL} from '@/components/bloodDraw/BloodDrawData/request.js';
import { orderBaseURL } from "@/utils/orderRequest";

import gql from "graphql-tag";
import { pns_graphal_client } from "@/utils/graphQLRequests.js";

export const useBloodDrawStore = defineStore('BloodDrawStore',{
    state:()=>{
        return{
            accession_id:null,
            sample_id:null,
            token:null,

            patient_id:null,
            customer_id:null,
            order_id:null,
            blood_draw_data: null,
            non_blood_draw_needed: null,
            collection_page_instructions: null,
            from_source: null, // might be "email", no value or other ENUMs in the future

            sample_types_mapping_backend_to_url_params:{
                "Stool" : 'Stool',
                "Saliva(4 tubes)": 'Saliva (4 tubes)',
                "Saliva(1 tube)": 'Saliva (1 tube)',
                "Microtube(Blood)": 'Microtube (Blood)',
                "Urine(4 tubes)":'Urine (4 tubes)',
                "Urine(1 tube)": 'Urine',
                "DBS Card": 'DBS Card',
            },

            cur_active_tab:'',
            country_options_for_phone:[],
            geo_info_of_selected_location:null,
            location_info_of_selected_location:null,
            page_loading:false,
        }
    },
    actions:{
        storeAllQueryInfomation(query){
            //temoprary add for debug in main
            console.log('storeAllQueryInfomation', query);
            if(query){
                this.accession_id = query.accession_id;
                this.sample_id = query.sample_id;
                this.token = query.token;
                this.from_source = query.from;

            }else{
                console.log('no query params');
                // if no query params: reject error
               throw new Error('No query params');
            }
        },
        async initiateBloodDrawSitePage(){
            try{
                const all_ids_data = await this.getAllIdsViaSampleId(this.sample_id);
                this.patient_id = all_ids_data?.patientId;
                this.order_id = all_ids_data?.orderIdStr;
                this.customer_id = all_ids_data?.customerId
                this.non_blood_draw_needed = all_ids_data?.nonBloodKitDeliveryMethod != null;

                const blood_draw_site_page_data = await this.getBloodDrawSitePageData(this.accession_id, this.sample_id, this.order_id, this.patient_id, this.customer_id);
                let blood_draw_data = blood_draw_site_page_data?.bloodDrawInfo;
                //need to shallow copy the value inside graphQL repsonse, as Vue will consider the original object as readOnly
                this.convertTabsOrder({...blood_draw_data}); 
                this.blood_draw_data = blood_draw_data;
                this.country_options_for_phone = blood_draw_site_page_data?.countryList;
            }catch(err){
                console.log('initiateBloodDrawSitePage Err',err);
                throw new Error(err?.message ?? "Failed to initiate Blood Draw Site Page.");
            }
        },
        // graphQL requests
        async getAllIdsViaSampleId(sample_id){
            // on PaymentMedia Page
            const GET_ALL_IDs_AND_ORDER_SOURCE_VIA_SAMPLE = gql `
            query getAllIdsAndOrderSourceViaSampleId( $sampleIdList:[String!] ){
                patientOrder(orderInput:{ sampleIdList: $sampleIdList }){
                    orders{
                        order{
                            patientId
                            orderIdStr
                            customerId
                            julienBarcode
                            sampleId
                            nonBloodKitDeliveryMethod
                        }
                    }
                }
            }
            `;
            try{
                const { data, errors } = await pns_graphal_client.query({
                    query: GET_ALL_IDs_AND_ORDER_SOURCE_VIA_SAMPLE,
                    variables: {
                    sampleIdList: [String(sample_id)]
                    },
                    context:{
                    headers:{
                        Authorization: `Bearer ${ this.token ? this.token : localStorage.getItem('pnsToken')}`
                    }
                    }
                });
                //As graphql won't trhow an error automatically, we need to manually check the error.
                if(errors && errors.length > 0){
                    throw new Error(errors.map(err => err.message).join(' | '));
                }
                return Promise.resolve(data?.patientOrder?.orders?.[0]?.order);
            }catch(err){
                console.log('getAllIdsAndOrderSourceViaSampleId Error: ', err);
                throw new Error(err?.message ?? 'Failed to get ids and order source.');
            }
        },
        async getBloodDrawSitePageData(accession_id, sample_id, order_id, patient_id, customer_id){
            const GET_BLOOD_DRAW_SITE_PAGE_DATA = gql `
            query getBloodDrawSitePageData( $accession_id: String!, $sample_id: String!, $order_id: String!, $patient_id: String!, $customer_id:String ){
                patientPNS( PNSInput: { accession_id: $accession_id, sample_id: $sample_id, order_id: $order_id, patient_id: $patient_id, customer_id:$customer_id }){
                    bloodDrawInfo {
                        tabs_order
                        in_office {
                            service_tags
                            open_hour
                            location_name
                            address
                            email
                            phone
                            website
                            geo {
                                location_id
                                type
                                coordinates
                            }
                        }
                        draw_site {
                            service_tags
                            open_hour
                            location_name
                            address
                            email
                            phone
                            website
                            geo {
                                location_id
                                type
                                coordinates
                            }
                        }
                        mobile {
                            service_tags
                            open_hour
                            location_name
                            address
                            email
                            phone
                            website
                            geo {
                                location_id
                                type
                                coordinates
                            }
                        }
                    }
                    countryList {
                        vibrantCountryId
                        countryName
                        countryNameTwoBytes
                    }
                }
            }
            `;
            try{
            const { data, errors } = await pns_graphal_client.query(
                {
                    query: GET_BLOOD_DRAW_SITE_PAGE_DATA,
                    variables:{
                        accession_id: String(accession_id),   
                        sample_id: String(sample_id),
                        order_id: String(order_id),
                        patient_id: String(patient_id),
                        customer_id: String(customer_id)
                    },
                    context:{
                        headers:{
                            Authorization: `Bearer ${this.token ? this.token : localStorage.getItem('pnsToken')}`
                        }
                    },
                    fetchPolicy: 'network-only'
                }
            )
            //As graphql won't trhow an error automatically, we need to manually check the error.
            if(errors && errors.length > 0){
                throw new Error(errors.map(err => err.message).join(' | '));
            }
                return Promise.resolve(data?.patientPNS);
            }catch(err){
                console.log('getBloodDrawSitePageData Error: ', err);
                throw new Error(err?.message ?? "Failed to get Blood Draw Site Page data.");
            }
        },
        storeCollectionPageInstructions(instructions){
            this.collection_page_instructions = instructions;
        },
        convertSampleTypesToNextStepParams(backend_sample_types){
            var output = '';
            for(const sample_type of backend_sample_types){
                const converted_sample_type = this.sample_types_mapping_backend_to_url_params[sample_type]
                ?
                this.sample_types_mapping_backend_to_url_params[sample_type].replaceAll(' ','+')
                :
                '';
                if(output){
                    output += `-${converted_sample_type}`;
                }else{
                    output = converted_sample_type;
                }
            }
            return output;
        },
        async getCollectionInstructions(){
            try{
                const res = await orderBaseURL.get('/nonBloodSampleTubeType',{
                    headers:{
                        Authorization: `Bearer ${localStorage.getItem('pnsToken') ? localStorage.getItem('pnsToken') : this.token}`
                    },
                    params:{
                        sampleId: Number(this.sample_id),
                    },
                });
                const instructions = this.convertSampleTypesToNextStepParams(res.data);
                this.collection_page_instructions = instructions;
                return instructions;
            }catch(err){
                console.log('getCollectionInstructions error', err);
                this.collection_page_instructions = '';
                return '';
            }
        },
        convertTabsOrder(data){
            const tabs_order_dict = {
                'INOFFICE':'in_office',
                'DRAW-SITE': 'draw_site',
                'MOBILE':'mobile',
            }
            //handle tabs_order == null, when this customer has no pns setting
            const converted_tabs_order = data['tabs_order']==null?['mobile','in_office','draw_site']:data['tabs_order'].map((item)=>{
                return tabs_order_dict[item];
            })
            data['tabs_order'] = converted_tabs_order;
            // console.log('convertTabsOrder', data['tabs_order']);
        },
        setCurActiveTab(val){
            // console.log('setCurActiveTab', val)
            this.cur_active_tab = val;
        },
        storeSelectedLocationGeoInfo(geo_info){
            // console.log('storeSelectedLocationGeoInfo', geo_info);
            this.geo_info_of_selected_location = geo_info;
        },
        storeSelectedLocationInfo(location_name, address){
            // console.log('storeSelectedLocationGeoInfo', geo_info);
            this.location_info_of_selected_location ={
                location_name: location_name,
                address:  address,
            };
        },
        async sendInfoToMyPhone(phone_number,location_name, address){
            const params ={
                phone_number:phone_number,
                text:`Blood Draw Site Name: ${location_name==null||location_name==''?'No Information':location_name}\nAddress: ${address==null||address ==''?'No Information':address}`,
            }
            try{
                const response = await baseURL.get('/utility/sendLocationText',{
                    headers:{ 
                        Authorization: `Bearer ${this.token}`
                    },
                    params:params,
                })
                return response.data.result == 'success';
                // return false;
            }catch(err){
                console.log('sendToMyPhone api error:', err);
                return false;
            }
        }

    },
});